import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Wallet, AlertCircle, Check, Save, ChevronDown } from 'lucide-react';
import { affiliateDashboardAPI } from '../../utils/api';

const PayoutDetailsPage = () => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isBankCountryOpen, setIsBankCountryOpen] = useState(false);
  const bankCountryDropdownRef = useRef(null);

  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState('');
  const [payoutDetails, setPayoutDetails] = useState({
    accountNumber: '',
    bankName: '',
    accountHolderName: '',
    iban: '',
    swiftCode: '',
    bankCountry: '',
    paypalEmail: '',
  });

  const countries = (() => {
    const base = ['Saudi Arabia', 'United Arab Emirates', 'Kuwait', 'Qatar', 'Bahrain', 'Oman', 'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Poland', 'Czech Republic', 'Hungary', 'Portugal', 'Greece', 'Turkey', 'Russia', 'Ukraine', 'Romania', 'Bulgaria', 'Croatia', 'Slovenia', 'Slovakia', 'Lithuania', 'Latvia', 'Estonia', 'Ireland', 'Iceland', 'Luxembourg', 'Malta', 'Cyprus', 'Australia', 'New Zealand', 'Japan', 'South Korea', 'China', 'India', 'Singapore', 'Malaysia', 'Thailand', 'Philippines', 'Indonesia', 'Vietnam', 'Brazil', 'Argentina', 'Chile', 'Mexico', 'Colombia', 'Peru', 'South Africa', 'Egypt', 'Morocco', 'Nigeria', 'Kenya', 'Ghana', 'Palestine', 'Jordan', 'Lebanon', 'Syria', 'Iraq', 'Iran', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Myanmar', 'Cambodia', 'Other'];
    const withoutPinned = base.filter(c => c !== 'Saudi Arabia' && c !== 'Other');
    withoutPinned.sort((a, b) => a.localeCompare(b));
    return ['Saudi Arabia', ...withoutPinned, 'Other'];
  })();

  useEffect(() => {
    loadPayoutDetails();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bankCountryDropdownRef.current && !bankCountryDropdownRef.current.contains(event.target)) {
        setIsBankCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPayoutDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await affiliateDashboardAPI.getPayoutDetails();
      setPreferredPaymentMethod(data.preferredPaymentMethod || '');
      if (data.payoutDetails) setPayoutDetails(prev => ({ ...prev, ...data.payoutDetails }));
    } catch (err) {
      console.error('Error loading payout details:', err);
      setError(t('affiliatePayout.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(false);

      let detailsToSave = {};
      if (preferredPaymentMethod === 'bank_transfer') {
        detailsToSave = {
          accountNumber: payoutDetails.accountNumber,
          bankName: payoutDetails.bankName,
          accountHolderName: payoutDetails.accountHolderName,
          iban: payoutDetails.iban,
          swiftCode: payoutDetails.swiftCode,
          bankCountry: payoutDetails.bankCountry,
        };
      } else if (preferredPaymentMethod === 'paypal') {
        detailsToSave = { paypalEmail: payoutDetails.paypalEmail };
      }

      await affiliateDashboardAPI.updatePayoutDetails(preferredPaymentMethod, detailsToSave);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving payout details:', err);
      setError(err.message || t('affiliatePayout.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const paymentMethods = [
    { value: 'bank_transfer', label: t('affiliatePayout.bankTransfer'), icon: Building2, description: t('affiliatePayout.bankTransferDescription') },
    { value: 'paypal', label: t('affiliatePayout.paypal'), icon: Wallet, description: t('affiliatePayout.paypalDescription') },
  ];

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('affiliatePayout.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
      <div className={`mb-6 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
        <h1 className="text-3xl font-bold text-foreground">{t('affiliatePayout.title')}</h1>
        <p className="mt-2 text-muted-foreground">
          {t('affiliatePayout.subtitle')}
        </p>
      </div>

      {error && (
        <div className={`mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            <p className={`${i18n.language === 'ar' ? 'mr-3' : 'ml-3'} text-sm text-destructive`}>{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className={`mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className={`${i18n.language === 'ar' ? 'mr-3' : 'ml-3'} text-sm text-green-600`}>
              {t('affiliatePayout.savedSuccess')}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="bg-card border border-border rounded-lg shadow p-6">
        <div className="mb-6">
          <label className={`block text-sm font-medium text-muted-foreground mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
            {t('affiliatePayout.preferredPaymentMethod')} *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paymentMethods.map((method) => {
              const IconComponent = method.icon;
              return (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPreferredPaymentMethod(method.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    preferredPaymentMethod === method.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  } ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                >
                  <div className={`flex items-start ${i18n.language === 'ar' ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <IconComponent className={`h-6 w-6 mt-1 ${preferredPaymentMethod === method.value ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className={`flex-1 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <h3 className="font-semibold text-card-foreground mb-1">{method.label}</h3>
                      <p className="text-sm text-muted-foreground">{method.description}</p>
                    </div>
                    {preferredPaymentMethod === method.value && (
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {preferredPaymentMethod === 'bank_transfer' && (
          <div className="space-y-4 mb-6 pt-6 border-t border-border">
            <h3 className={`text-lg font-semibold text-card-foreground mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              {t('affiliatePayout.bankAccountDetails')}
            </h3>
            
            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.accountHolderName')} *
              </label>
              <input
                type="text"
                required
                value={payoutDetails.accountHolderName}
                onChange={(e) => setPayoutDetails({ ...payoutDetails, accountHolderName: e.target.value })}
                className={`w-full glass-input ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder={t('affiliatePayout.accountHolderPlaceholder')}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.bankName')} *
              </label>
              <input
                type="text"
                required
                value={payoutDetails.bankName}
                onChange={(e) => setPayoutDetails({ ...payoutDetails, bankName: e.target.value })}
                className={`w-full glass-input ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder={t('affiliatePayout.bankNamePlaceholder')}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.accountNumber')} *
              </label>
              <input
                type="text"
                required
                value={payoutDetails.accountNumber}
                onChange={(e) => setPayoutDetails({ ...payoutDetails, accountNumber: e.target.value })}
                className={`w-full glass-input ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder="1234567890"
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.bankCountry')} *
              </label>
              <div className="relative" ref={bankCountryDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsBankCountryOpen(!isBankCountryOpen)}
                  className={`w-full px-4 py-3 border border-border rounded-lg bg-background hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 flex items-center justify-between ${
                    i18n.language === 'ar' ? 'text-right flex-row-reverse' : 'text-left'
                  }`}
                >
                  <span className={`${payoutDetails.bankCountry ? 'text-foreground' : 'text-muted-foreground'} truncate`}>
                    {payoutDetails.bankCountry || t('affiliatePayout.selectCountry')}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isBankCountryOpen ? 'rotate-180' : ''}`} />
                </button>

                {isBankCountryOpen && (
                  <div className={`absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto ${
                    i18n.language === 'ar' ? 'right-0' : 'left-0'
                  }`}>
                    {countries.map((country) => (
                      <button
                        key={country}
                        type="button"
                        onClick={() => {
                          setPayoutDetails({ ...payoutDetails, bankCountry: country });
                          setIsBankCountryOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-muted flex items-center justify-between ${
                          payoutDetails.bankCountry === country ? 'bg-primary/10' : ''
                        } ${i18n.language === 'ar' ? 'text-right flex-row-reverse' : 'text-left'}`}
                      >
                        <span className="text-sm font-medium text-card-foreground">
                          {country}
                        </span>
                        {payoutDetails.bankCountry === country && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.iban')} *
              </label>
              <input
                type="text"
                required
                value={payoutDetails.iban}
                onChange={(e) => setPayoutDetails({ ...payoutDetails, iban: e.target.value })}
                className={`w-full glass-input ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder="GB82 WEST 1234 5698 7654 32"
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.swiftOptional')}
              </label>
              <input
                type="text"
                value={payoutDetails.swiftCode}
                onChange={(e) => setPayoutDetails({ ...payoutDetails, swiftCode: e.target.value })}
                className={`w-full glass-input ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder="BICFNOKKXXX"
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>
          </div>
        )}

        {preferredPaymentMethod === 'paypal' && (
          <div className="space-y-4 mb-6 pt-6 border-t border-border">
            <h3 className={`text-lg font-semibold text-card-foreground mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              {t('affiliatePayout.paypalDetails')}
            </h3>
            
            <div>
              <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('affiliatePayout.paypalEmail')} *
              </label>
              <input
                type="email"
                required
                value={payoutDetails.paypalEmail}
                onChange={(e) => setPayoutDetails({ ...payoutDetails, paypalEmail: e.target.value })}
                className={`w-full glass-input ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder="your.email@example.com"
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
            </div>
          </div>
        )}

        {preferredPaymentMethod && (
          <div className={`flex ${i18n.language === 'ar' ? 'justify-start' : 'justify-end'} pt-6 border-t border-border`}>
            <button
              type="submit"
              disabled={isSaving}
              className="glass-button px-6 py-3 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  {t('affiliatePayout.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t('affiliatePayout.savePayoutDetails')}
                </>
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PayoutDetailsPage;

